import React, { useState, useEffect, useRef, useCallback } from 'react';
import api, { getClients, cashTopup, getSites, getBusinessEmployeesList, getBusinessWalletSummary } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Banknote, User, Wallet, CheckCircle, Printer, FileText, CreditCard, Building2, Search, X, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, extractApiErrorMessage } from '../lib/utils';
import { useDebounce } from '../hooks/useDebounce';

// Company info for invoice
const COMPANY_INFO = {
  name: "C.S.M Sh.p.k.",
  address: "Tirane Vore Muçaj Rruga dytesore, Tirane -Vore, 1032, Vore, Tirane",
  city: "Tiranë, Shqipëri",
  nipt: "L12345678A",
  phone: "+355 68 200 1818",
  email: "info@albmove.al",
  website: "www.albmove.al",
  bankName: "Raiffeisen Bank",
  bankAccount: "AL35202111090000000001234567"
};

// Payment methods
const PAYMENT_METHODS = [
  { id: 'cash', label: 'Cash', icon: Banknote },
  { id: 'bank_card', label: 'Kartë Bankare', icon: CreditCard },
  { id: 'bank_transfer', label: 'Transfer Bankar', icon: Building2 },
];

// Format date in European format
const formatEuropeanDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('sq-AL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

// Generate receipt number
const generateReceiptNumber = (transaction) => {
  const date = new Date(transaction?.created_at || new Date());
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const randomNum = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
  return `RCP-${year}${month}-${randomNum}`;
};

// Get payment method label
const getPaymentMethodLabel = (methodId) => {
  const method = PAYMENT_METHODS.find(m => m.id === methodId);
  return method ? method.label : methodId;
};

const CashTopup = () => {
  const { user } = useAuth();
  const isBusiness = user?.role === 'BUSINESS';
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [noResults, setNoResults] = useState(false);
  
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [formData, setFormData] = useState({
    user_id: '',
    site_id: '',
    amount: '',
    wallet_type: 'restricted',
    payment_method: 'cash',
    notes: ''
  });
  const [selectedClient, setSelectedClient] = useState(null);
  const [businessWallet, setBusinessWallet] = useState(null);
  const businessAvailableBalance = Number(businessWallet?.business_wallet?.balance || 0);
  
  // Receipt state
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [lastTransaction, setLastTransaction] = useState(null);
    const formatCreditsQuantity = (value) => {
      const amount = Number(value || 0);
      if (!Number.isFinite(amount)) return '0 Kredite';
      const formatted = amount.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
      });
      return `${formatted} Kredite`;
    };

  const receiptRef = useRef(null);
  const searchRef = useRef(null);

  const loadBusinessWallet = useCallback(async () => {
    if (!isBusiness) return;
    try {
      const res = await getBusinessWalletSummary();
      setBusinessWallet(res?.data || null);
    } catch (error) {
      console.error('Failed to load business wallet:', error);
    }
  }, [isBusiness]);
  
  // Debounce search query
  const debouncedSearch = useDebounce(searchQuery, 300);

  useEffect(() => {
    const loadSites = async () => {
      try {
        const response = await getSites();
        const siteItems = Array.isArray(response.data) ? response.data : response.data?.sites || [];
        const sortedSites = [...siteItems].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'sq'));
        setSites(sortedSites);
        setFormData((prev) => ({
          ...prev,
          site_id: prev.site_id || (sortedSites.length === 1 ? sortedSites[0].id : ''),
        }));
      } catch (error) {
        console.error('Failed to load sites:', error);
        toast.error('Gabim në ngarkimin e stacioneve');
      } finally {
        setSitesLoading(false);
      }
    };

    loadSites();
  }, []);

  useEffect(() => {
    loadBusinessWallet();
  }, [loadBusinessWallet]);

  // Search clients when debounced query changes
  useEffect(() => {
    const searchClients = async () => {
      if (!debouncedSearch || debouncedSearch.length < 2) {
        setSearchResults([]);
        setNoResults(false);
        setShowDropdown(false);
        return;
      }
      
      setSearching(true);
      setNoResults(false);
      try {
        if (isBusiness) {
          const response = await getBusinessEmployeesList();
          const employees = Array.isArray(response?.data?.employees) ? response.data.employees : [];
          const clients = employees
            .map((emp) => ({
              id: emp.id,
              client_id: emp.employee_account_id || emp.employee_id,
              full_name: emp.employee_name,
              email: emp.employee_email,
              phone: emp.employee_phone || '',
              wallet_balance: Number(emp?.wallet?.balance || 0),
            }))
            .filter((c) => {
              const q = debouncedSearch.toLowerCase();
              return (
                String(c.full_name || '').toLowerCase().includes(q) ||
                String(c.email || '').toLowerCase().includes(q) ||
                String(c.phone || '').toLowerCase().includes(q)
              );
            })
            .slice(0, 10);
          setSearchResults(clients);
          setNoResults(clients.length === 0);
        } else {
          const response = await getClients({ search: debouncedSearch, limit: 10 });
          const clients = response.data?.items || response.data || [];
          setSearchResults(clients);
          setNoResults(clients.length === 0);
        }
        setShowDropdown(true);
      } catch (error) {
        console.error('Search failed:', error);
        setSearchResults([]);
        setNoResults(true);
      } finally {
        setSearching(false);
      }
    };
    
    searchClients();
  }, [debouncedSearch, isBusiness]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle client selection
  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setFormData({ ...formData, user_id: client.id });
    setSearchQuery(client.full_name);
    setShowDropdown(false);
    setSuccess(false);
  };

  // Clear selection
  const handleClearSelection = () => {
    setSelectedClient(null);
    setFormData({ ...formData, user_id: '' });
    setSearchQuery('');
    setSearchResults([]);
    setNoResults(false);
  };

  // Print receipt function
  const handlePrintReceipt = () => {
    if (!receiptRef.current) return;
    
    const printContent = receiptRef.current.innerHTML;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Faturë Top-up - AlbMove</title>
          <script src="https://cdn.tailwindcss.com"></script>
          <style>
            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body class="bg-white p-4">
          ${printContent}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isRestrictedTopup = formData.wallet_type !== 'global';
    if (!formData.user_id || !formData.amount || (isRestrictedTopup && !formData.site_id)) {
      toast.error('Plotësoni të gjitha fushat e detyrueshme');
      return;
    }

    const amountValue = parseFloat(formData.amount || 0);
    if (isBusiness && amountValue > businessAvailableBalance) {
      toast.error('Shuma tejkalon balancën në dispozicion të biznesit');
      return;
    }

    setSubmitting(true);
    try {
      let response;
      if (isBusiness) {
        response = await api.post(`/v1/business/employees/${formData.user_id}/allocate`, {
          employee_id: formData.user_id,
          site_id: formData.site_id,
          amount_lek: amountValue,
          reason: formData.notes || 'Alokim nga biznesi'
        });
      } else {
        response = await cashTopup(
          formData.user_id,
          amountValue,
          isRestrictedTopup ? formData.site_id : null,
          formData.notes,
          formData.wallet_type
        );
      }
      toast.success('Mbushja u krye me sukses');
      setSuccess(true);
      const selectedSite = sites.find((site) => site.id === formData.site_id);
      
      // Store transaction details for receipt
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      setLastTransaction({
        user: selectedClient,
        amount: amountValue,
        new_balance: response?.data?.total_display_balance || response?.data?.employee_balance || response?.data?.total_display_balance,
        old_balance: selectedClient?.wallet_balance || 0,
        site_name: isRestrictedTopup
          ? (response?.data?.site_name || selectedSite?.name || 'Stacion i zgjedhur')
          : 'Balancë globale',
        restricted_balance: response?.data?.new_restricted_balance,
        wallet_type: formData.wallet_type,
        payment_method: formData.payment_method,
        notes: formData.notes,
        created_at: new Date().toISOString(),
        processed_by: currentUser.full_name || currentUser.email || 'Admin'
      });
      
      // Open receipt dialog
      setReceiptOpen(true);
      
      // Reset form
      setFormData((prev) => ({
        user_id: '',
        site_id: prev.site_id,
        amount: '',
        wallet_type: 'restricted',
        payment_method: 'cash',
        notes: ''
      }));
      setSelectedClient(null);
      setSearchQuery('');
      if (isBusiness) {
        await loadBusinessWallet();
      }
      
    } catch (error) {
      console.error('Failed to process topup:', error);
      toast.error(extractApiErrorMessage(error, 'Gabim në mbushjen e wallet-it'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="cash-topup-page">
      <div>
        <h1 className="text-2xl font-bold text-white font-outfit">{isBusiness ? 'Shpërndarje Balancash' : 'Mbushje Wallet'}</h1>
        <p className="text-zinc-400 mt-1">{isBusiness ? 'Shpërndani balancën tek punonjësit tuaj për stacionin e zgjedhur' : 'Mbushni wallet-in e klientëve me cash ose kartë'}</p>
      </div>

      {isBusiness && (
        <Card className="bg-zinc-900/50 border-emerald-500/30">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-zinc-400">Balanca në dispozicion</p>
                <p className="text-2xl font-bold text-emerald-400">{formatCurrency(businessAvailableBalance)}</p>
                <p className="text-xs text-zinc-500 mt-1">Kjo shumë alokohet në balancë të kufizuar sipas stacionit</p>
              </div>
              <div>
                <p className="text-sm text-zinc-400">Alokuar Punonjësve</p>
                <p className="text-2xl font-bold text-blue-400">{formatCurrency(businessWallet?.employee_wallets?.total_allocated || 0)}</p>
              </div>
              <div>
                <p className="text-sm text-zinc-400">Punonjës</p>
                <p className="text-2xl font-bold text-white">{businessWallet?.employee_wallets?.count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form Card */}
        <Card className="bg-zinc-900/50 border-white/10">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <CardTitle className="text-white font-outfit">Shto Kredite</CardTitle>
                <CardDescription>Regjistroni blerjen e krediteve AlbMove</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Client Search */}
              <div className="space-y-2">
                <Label>Klienti</Label>
                <div className="relative" ref={searchRef}>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        if (selectedClient && e.target.value !== selectedClient.full_name) {
                          setSelectedClient(null);
                          setFormData({ ...formData, user_id: '' });
                        }
                      }}
                      onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
                      placeholder="Kërko klientin (emri, email, telefon)..."
                      className="pl-10 pr-10"
                      data-testid="client-search-input"
                    />
                    {searching && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 animate-spin" />
                    )}
                    {selectedClient && !searching && (
                      <button
                        type="button"
                        onClick={handleClearSelection}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  
                  {/* Search Results Dropdown */}
                  {showDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-zinc-900 border border-white/10 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                      {searchResults.length > 0 ? (
                        searchResults.map((client) => (
                          <button
                            key={client.id}
                            type="button"
                            onClick={() => handleSelectClient(client)}
                            className="w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors text-left"
                            data-testid={`client-option-${client.id}`}
                          >
                            <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
                              <User className="w-5 h-5 text-zinc-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-white font-medium truncate">{client.full_name}</p>
                              <p className="text-xs text-zinc-500 truncate">{client.email}</p>
                              {client.phone && (
                                <p className="text-xs text-zinc-500">{client.phone}</p>
                              )}
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-sm text-emerald-400 font-medium">
                                {formatCurrency(client.wallet_balance || 0)}
                              </p>
                              <p className="text-xs text-zinc-500">Bilanci</p>
                            </div>
                          </button>
                        ))
                      ) : noResults ? (
                        <div className="p-4 text-center">
                          <AlertCircle className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                          <p className="text-orange-400 font-medium">Klienti nuk u gjet</p>
                          <p className="text-xs text-zinc-500 mt-1">
                            Nuk ekziston asnjë klient me këtë emër, email ose telefon
                          </p>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
                
                {/* Selected Client Badge */}
                {selectedClient && (
                  <div className="flex items-center gap-2 p-2 bg-green-500/10 border border-green-500/20 rounded-lg">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-green-400 text-sm">
                      Klienti i zgjedhur: <span className="font-medium">{selectedClient.full_name}</span>
                    </span>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="amount">Shuma (LEK)</Label>
                <div className="relative">
                  <Input
                    id="amount"
                    type="number"
                    step="1"
                    min="1"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0"
                    className="pr-12 text-lg"
                    required
                    data-testid="amount-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-zinc-500">
                    LEK
                  </span>
                </div>
                {/* Quick amounts */}
                <div className="flex flex-wrap gap-2 mt-2">
                  {[1000, 2000, 5000, 10000].map((amt) => (
                    <button
                      key={amt}
                      type="button"
                      onClick={() => setFormData({ ...formData, amount: amt.toString() })}
                      className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                        formData.amount === amt.toString() 
                          ? 'border-green-500 bg-green-500/20 text-green-400' 
                          : 'border-white/10 text-zinc-400 hover:border-white/30'
                      }`}
                    >
                      {amt.toLocaleString()} LEK
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Lloji i Balancës</Label>
                <Select
                  value={formData.wallet_type}
                  onValueChange={(value) => {
                    const nextWalletType = value || 'restricted';
                    setFormData({
                      ...formData,
                      wallet_type: nextWalletType,
                      site_id: nextWalletType === 'global' ? '' : formData.site_id,
                    });
                  }}
                >
                  <SelectTrigger data-testid="wallet-type-select-trigger">
                    <SelectValue placeholder="Zgjidh llojin e balancës" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="restricted">Balancë e kufizuar (sipas stacionit)</SelectItem>
                    <SelectItem value="global">Balancë globale</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-zinc-500">
                  {formData.wallet_type === 'global'
                    ? 'Kjo mbushje shkon direkt në balancën globale të klientit.'
                    : 'Kjo mbushje krijon balancë të kufizuar që përdoret vetëm në stacionin e zgjedhur.'}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Stacioni</Label>
                <Select
                  value={formData.site_id}
                  onValueChange={(value) => setFormData({ ...formData, site_id: value })}
                  disabled={formData.wallet_type === 'global' || sitesLoading || sites.length === 0}
                >
                  <SelectTrigger data-testid="site-select-trigger">
                    <SelectValue
                      placeholder={
                        formData.wallet_type === 'global'
                          ? 'Nuk kërkohet për balancën globale'
                          : (sitesLoading ? 'Duke ngarkuar stacionet...' : 'Zgjidh stacionin')
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.map((site) => (
                      <SelectItem key={site.id} value={site.id}>
                        {site.name || site.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-zinc-500">
                  {formData.wallet_type === 'global'
                    ? 'Për balancën globale, stacioni nuk është i detyrueshëm.'
                    : 'Kjo mbushje cash krijon balancë të kufizuar që përdoret vetëm në stacionin e zgjedhur.'}
                </p>
              </div>

              {/* Payment Method */}
              <div className="space-y-2">
                <Label>Metoda e Pagesës</Label>
                <div className="grid grid-cols-3 gap-2">
                  {PAYMENT_METHODS.map((method) => {
                    const Icon = method.icon;
                    return (
                      <button
                        key={method.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, payment_method: method.id })}
                        className={`flex flex-col items-center gap-2 p-3 rounded-lg border transition-all ${
                          formData.payment_method === method.id
                            ? 'border-green-500 bg-green-500/10 text-green-400'
                            : 'border-white/10 text-zinc-400 hover:border-white/30'
                        }`}
                        data-testid={`payment-method-${method.id}`}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="text-xs">{method.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Shënime (opsional)</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Shënime për transaksionin..."
                  rows={2}
                  data-testid="notes-input"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-green-600 hover:bg-green-700" 
                disabled={submitting || !formData.user_id || !formData.amount || (formData.wallet_type !== 'global' && !formData.site_id)}
                data-testid="submit-topup-btn"
              >
                {submitting ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Duke procesuar...
                  </div>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Konfirmo Mbushjen
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Preview Card */}
        <Card className="bg-zinc-900/50 border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-outfit">Përmbledhje</CardTitle>
            <CardDescription>Shikim paraprak i transaksionit</CardDescription>
          </CardHeader>
          <CardContent>
            {selectedClient ? (
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 rounded-lg bg-zinc-800/50">
                  <div className="w-12 h-12 rounded-full bg-zinc-700 flex items-center justify-center">
                    <User className="w-6 h-6 text-zinc-400" />
                  </div>
                  <div>
                    <p className="font-medium text-white">{selectedClient.full_name}</p>
                    <p className="text-sm text-zinc-500">{selectedClient.email}</p>
                    {selectedClient.phone && (
                      <p className="text-sm text-zinc-500">{selectedClient.phone}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 rounded-lg bg-zinc-800/50">
                    <span className="text-zinc-400">Bilanci aktual</span>
                    <span className="text-lg font-semibold text-white">
                      {formatCurrency(selectedClient.wallet_balance || 0)}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                    <span className="text-green-400">
                      {formData.wallet_type === 'global' ? 'Kredite në balancë globale' : 'Kredite për stacion'}
                    </span>
                    <span className="text-lg font-semibold text-green-400">
                      +{formatCurrency(parseFloat(formData.amount) || 0)}
                    </span>
                  </div>

                  <div className="flex justify-between items-center p-3 rounded-lg bg-zinc-800/50">
                    <span className="text-zinc-400">Destinacioni</span>
                    <span className="text-white text-right max-w-[60%] truncate">
                      {formData.wallet_type === 'global'
                        ? 'Balancë globale'
                        : (sites.find((site) => site.id === formData.site_id)?.name || 'Zgjidh stacionin')}
                    </span>
                  </div>

                  <div className="flex justify-between items-center p-3 rounded-lg bg-zinc-800/50">
                    <span className="text-zinc-400">Metoda pagesës</span>
                    <span className="text-white">
                      {getPaymentMethodLabel(formData.payment_method)}
                    </span>
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
                  <p className="text-sm text-zinc-400">Bilanci i ri</p>
                  <p className="text-2xl font-bold text-primary mt-1">
                    {formatCurrency((selectedClient.wallet_balance || 0) + (parseFloat(formData.amount) || 0))}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Wallet className="w-12 h-12 text-zinc-600 mb-4" />
                <p className="text-zinc-500">Kërkoni dhe zgjidhni një klient për të parë detajet</p>
              </div>
            )}

            {success && (
              <div className="mt-4 p-4 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-green-400">Mbushja u krye me sukses!</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Receipt Dialog */}
      <Dialog open={receiptOpen} onOpenChange={setReceiptOpen}>
        <DialogContent className="max-w-2xl bg-zinc-900 border-white/10">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle className="text-white font-outfit flex items-center gap-2">
              <FileText className="w-5 h-5 text-green-500" />
              Faturë Blerje Kreditesh
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handlePrintReceipt}
                className="border-white/20 hover:bg-white/10"
              >
                <Printer className="w-4 h-4 mr-2" />
                Printo
              </Button>
            </div>
          </DialogHeader>
          
          {lastTransaction && (
            <div className="max-h-[70vh] overflow-y-auto">
              <div 
                ref={receiptRef} 
                className="bg-white text-gray-900 p-8"
                style={{ minHeight: '650px' }}
              >
                {/* HEADER: Logo Left, Company Info Right */}
                <div className="flex justify-between items-start mb-6">
                  <div className="flex-shrink-0">
                    <img 
                      src="/logo.png" 
                      alt="AlbMove" 
                      className="h-16"
                    />
                    <p className="text-xs text-gray-400 mt-1">Sistemi i Karikimit Elektrik</p>
                  </div>
                  
                  <div className="text-right">
                    <h2 className="text-xl font-bold text-[#1d62a7]">{COMPANY_INFO.name}</h2>
                    <p className="text-sm text-gray-600">{COMPANY_INFO.address}</p>
                    <p className="text-sm text-gray-600">{COMPANY_INFO.city}</p>
                    <p className="text-sm text-gray-600">NIPT: {COMPANY_INFO.nipt}</p>
                    <p className="text-sm text-gray-600">Tel: {COMPANY_INFO.phone}</p>
                    
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex justify-end gap-6">
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Nr. Faturës</p>
                          <p className="font-bold text-[#1d62a7]">{generateReceiptNumber(lastTransaction)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Data</p>
                          <p className="font-medium">{formatEuropeanDate(lastTransaction.created_at)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="h-1 bg-gradient-to-r from-[#10b981] to-[#059669] rounded-full mb-6"></div>

                {/* Client Info */}
                <div className="mb-6">
                  <h3 className="text-xs font-semibold text-[#10b981] uppercase tracking-wider mb-2">
                    Faturuar Drejt
                  </h3>
                  <div className="bg-gray-50 p-4 rounded-lg inline-block min-w-[250px]">
                    <p className="font-bold text-lg text-gray-900">{lastTransaction.user?.full_name || 'Klient'}</p>
                    {lastTransaction.user?.email && (
                      <p className="text-gray-600 text-sm">{lastTransaction.user.email}</p>
                    )}
                    {lastTransaction.user?.phone && (
                      <p className="text-gray-600 text-sm">{lastTransaction.user.phone}</p>
                    )}
                  </div>
                </div>

                {/* Invoice Items Table */}
                <div className="mb-6">
                  <h3 className="text-xs font-semibold text-[#10b981] uppercase tracking-wider mb-2">
                    Detajet e Faturës
                  </h3>
                  <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                    <thead>
                      <tr className="bg-[#10b981] text-white">
                        <th className="text-left py-3 px-4 font-semibold">Përshkrimi</th>
                        <th className="text-center py-3 px-4 font-semibold">Sasia</th>
                        <th className="text-right py-3 px-4 font-semibold">Çmimi</th>
                        <th className="text-right py-3 px-4 font-semibold">Totali</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <td className="py-4 px-4">
                          <p className="font-medium text-gray-900">Kredite AlbMove</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {`Blere ${formatCreditsQuantity(lastTransaction.amount)}, 1 Kredit = 1 Lek`}
                          </p>
                        </td>
                        <td className="text-center py-4 px-4 font-medium">{formatCreditsQuantity(lastTransaction.amount)}</td>
                        <td className="text-right py-4 px-4">1.00 LEK</td>
                        <td className="text-right py-4 px-4 font-bold text-[#10b981]">
                          {formatCurrency(lastTransaction.amount)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Payment & Totals */}
                <div className="grid grid-cols-2 gap-6 mb-6">
                  {/* Payment Method */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                      Metoda e Pagesës
                    </h3>
                    <div className="flex items-center gap-3">
                      {lastTransaction.payment_method === 'cash' && <Banknote className="w-6 h-6 text-green-600" />}
                      {lastTransaction.payment_method === 'bank_card' && <CreditCard className="w-6 h-6 text-blue-600" />}
                      {lastTransaction.payment_method === 'bank_transfer' && <Building2 className="w-6 h-6 text-purple-600" />}
                      <span className="font-medium text-gray-900">{getPaymentMethodLabel(lastTransaction.payment_method)}</span>
                    </div>
                  </div>

                  {/* Total */}
                  <div>
                    <div className="bg-gray-50 rounded-lg overflow-hidden">
                      <div className="flex justify-between py-2 px-4 border-b border-gray-200">
                        <span className="text-gray-600">Nëntotali</span>
                        <span className="font-medium">{formatCurrency(lastTransaction.amount)}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center py-3 px-4 bg-gradient-to-r from-[#10b981] to-[#059669] text-white rounded-lg mt-2">
                      <span className="font-semibold">TOTALI</span>
                      <span className="text-xl font-bold">{formatCurrency(lastTransaction.amount)}</span>
                    </div>
                  </div>
                </div>

                {/* Balance Info */}
                <div className="bg-blue-50 p-4 rounded-lg mb-6 border border-blue-200">
                  <h3 className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2">
                    Informacion Bilanci
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
                    <div>
                      <span className="text-gray-500">Bilanci para:</span>
                      <p className="font-medium text-gray-900">{formatCurrency(lastTransaction.old_balance)}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Kredite të shtuara:</span>
                      <p className="font-medium text-green-600">+{formatCreditsQuantity(lastTransaction.amount)}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Stacioni:</span>
                      <p className="font-medium text-gray-900">{lastTransaction.site_name}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Bilanci i ri:</span>
                      <p className="font-bold text-[#1d62a7]">{formatCurrency(lastTransaction.new_balance)}</p>
                    </div>
                  </div>
                </div>

                {/* Notes if any */}
                {lastTransaction.notes && (
                  <div className="mb-6">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                      Shënime
                    </h3>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-gray-700 text-sm">{lastTransaction.notes}</p>
                    </div>
                  </div>
                )}

                {/* Processed by & Status */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-full text-sm font-medium">
                    <CheckCircle className="w-4 h-4" />
                    E Paguar
                  </div>
                  <div className="text-sm text-gray-500">
                    Procesuar nga: <span className="font-medium text-gray-700">{lastTransaction.processed_by}</span>
                  </div>
                </div>

                {/* Footer */}
                <div className="pt-4 border-t border-gray-200 text-center">
                  <p className="text-[#10b981] font-semibold">Faleminderit që zgjodhët AlbMove!</p>
                  <p className="text-gray-500 text-xs mt-2">
                    {COMPANY_INFO.website} | {COMPANY_INFO.email} | {COMPANY_INFO.phone}
                  </p>
                  <p className="text-gray-400 text-xs mt-3">
                    Kjo faturë është gjeneruar automatikisht dhe është e vlefshme pa nënshkrim.
                  </p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CashTopup;
